// ============================================================================
// BP-09-04: Mentor Types
// BrightPath Community - Peer Mentoring Feature
// ============================================================================

import { z } from 'zod';

// =============================================================================
// Enums
// =============================================================================

export const MentorStatusEnum = z.enum([
  'pending',
  'active',
  'paused',
  'inactive',
]);
export type MentorStatus = z.infer<typeof MentorStatusEnum>;

export const MentorMatchStatusEnum = z.enum([
  'pending',
  'accepted',
  'declined',
  'active',
  'completed',
  'cancelled',
]);
export type MentorMatchStatus = z.infer<typeof MentorMatchStatusEnum>;

export const ContactMethodEnum = z.enum([
  'platform',
  'email',
  'phone',
  'video',
]);
export type ContactMethod = z.infer<typeof ContactMethodEnum>;

// =============================================================================
// Mentor Profile
// =============================================================================

export const MentorUserSchema = z.object({
  id: z.string().uuid(),
  first_name: z.string(),
  last_name: z.string(),
  avatar_url: z.string().nullable(),
});

export type MentorUser = z.infer<typeof MentorUserSchema>;

export const MentorDiagnosisSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
});

export type MentorDiagnosis = z.infer<typeof MentorDiagnosisSchema>;

export const MentorSchema = z.object({
  id: z.string().uuid(),
  user: MentorUserSchema,
  bio: z.string(),
  experience_years: z.number(),
  diagnoses: z.array(MentorDiagnosisSchema),
  expertise_areas: z.array(z.string()),
  is_accepting_matches: z.boolean(),
  max_mentees: z.number(),
  current_mentee_count: z.number(),
  contact_methods: z.array(ContactMethodEnum),
  availability_notes: z.string().nullable(),
  languages: z.array(z.string()),
  is_verified: z.boolean(),
  verification_date: z.string().datetime().nullable(),
  total_matches: z.number(),
  avg_rating: z.number().nullable(),
  status: MentorStatusEnum,
  created_at: z.string().datetime(),
});

export type Mentor = z.infer<typeof MentorSchema>;

export const MentorListItemSchema = MentorSchema.omit({
  bio: true,
  availability_notes: true,
}).extend({
  bio_excerpt: z.string(),
});

export type MentorListItem = z.infer<typeof MentorListItemSchema>;

// =============================================================================
// Mentor Match
// =============================================================================

export const MentorMatchSchema = z.object({
  id: z.string().uuid(),
  mentor: MentorSchema,
  mentee_user: MentorUserSchema,
  created_by: MentorUserSchema.nullable().optional(),
  status: MentorMatchStatusEnum,
  matched_at: z.string().datetime(),
  started_at: z.string().datetime().nullable(),
  ended_at: z.string().datetime().nullable(),
  match_reason: z.string().nullable(),
  mentor_notes: z.string().nullable(),
  mentee_notes: z.string().nullable(),
  satisfaction_rating: z.number().nullable(),
  feedback: z.string().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type MentorMatch = z.infer<typeof MentorMatchSchema>;

// =============================================================================
// Create/Update Schemas
// =============================================================================

export const MentorApplicationSchema = z.object({
  bio: z.string().min(50, 'Bio must be at least 50 characters').max(2000),
  experience_years: z.number().min(1, 'At least 1 year of experience required').max(50),
  diagnosis_ids: z.array(z.string().uuid()).min(1, 'Select at least one diagnosis'),
  expertise_areas: z.array(z.string()).min(1, 'Add at least one area of expertise'),
  contact_methods: z.array(ContactMethodEnum).min(1, 'Select at least one contact method'),
  max_mentees: z.number().min(1).max(10),
  availability_notes: z.string().max(500).optional(),
  languages: z.array(z.string()).min(1, 'Select at least one language'),
});

export type MentorApplicationInput = z.infer<typeof MentorApplicationSchema>;

export const UpdateMentorProfileSchema = MentorApplicationSchema.partial().extend({
  is_accepting_matches: z.boolean().optional(),
});

export type UpdateMentorProfileInput = z.infer<typeof UpdateMentorProfileSchema>;

export const MatchRequestSchema = z.object({
  mentor_id: z.string().uuid(),
  match_reason: z.string().min(20, 'Please explain why you want to connect').max(1000),
});

export type MatchRequestInput = z.infer<typeof MatchRequestSchema>;

export const MatchFeedbackSchema = z.object({
  satisfaction_rating: z.number().min(1).max(5),
  feedback: z.string().max(1000).optional(),
});

export type MatchFeedbackInput = z.infer<typeof MatchFeedbackSchema>;

// =============================================================================
// Query Parameters
// =============================================================================

export interface MentorSearchParams {
  diagnosis_id?: string;
  expertise?: string;
  language?: string;
  is_accepting?: boolean;
  page?: number;
  limit?: number;
}

// =============================================================================
// Response Types
// =============================================================================

export interface MentorsResponse {
  mentors: MentorListItem[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface MentorMatchesResponse {
  as_mentor: MentorMatch[];
  as_mentee: MentorMatch[];
}

export interface MentorStatsResponse {
  total_mentors: number;
  active_matches: number;
  successful_matches: number;
  diagnoses_covered: number;
}
